import React, { useState } from "react";
import { useIntl } from "react-intl";
import IOrder_Progress_State from "../../../../models/IOrder_Progress_State";
import ISupplierModel from "../../../../models/ISupplierModel";
import OrderStatesEnum from "../../../../models/OrderStatesEnum";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwButton from "../../../shared/OwButton/OwButton";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import {
    ATTACHMENT_KEYS,
    isPreliminaryStep,
    joinEmails,
    RECIPIENT_KEYS,
    splitEmails,
    stepThatSets,
    WorkflowCarryKey,
} from "./workflowSteps";

type StepKey = keyof IOrder_Progress_State;

type OptionRowProps = {
    /** Translation key of the label. */
    labelKey: string;
    helpKey?: string;
    name: string;
    checked: boolean;
    disabled?: boolean;
    /** Why the toggle is disabled; rendered as a chip before it. */
    reason?: React.ReactNode;
    onChange: (checked: boolean) => void;
    supplierModel: ISupplierModel;
    children?: React.ReactNode;
    nested?: boolean;
    /** Leave "vendor" as written: the label means a purchasing vendor source, not the renamed customer-facing vendor. */
    keepVendorWord?: boolean;
};

/** One step option: label and help on the left, a compact toggle on the right, nested rows below while on. */
export const WorkflowOptionRow = (props: OptionRowProps) => {
    return (
        <div
            className={["WorkflowOption", props.disabled ? "disabled" : "", props.nested ? "nested" : ""].filter(Boolean).join(" ")}
            data-testid={`wf-opt-${props.name}`}>
            <div className={"WorkflowOption-text"}>
                <div className={"WorkflowOption-label"}>
                    <OwLabel
                        title={props.labelKey}
                        noBottomMargin={true}
                        isSupplierVendorOverride={!props.keepVendorWord}
                        supplierModel={props.supplierModel}
                    />
                </div>
                {props.helpKey ? (
                    <div className={"WorkflowOption-help"}>
                        <OwLabel
                            title={props.helpKey}
                            noBottomMargin={true}
                            isSupplierVendorOverride={!props.keepVendorWord}
                            supplierModel={props.supplierModel}
                        />
                    </div>
                ) : null}
            </div>
            <div className={"WorkflowOption-control"}>
                {props.reason}
                <OwToggle
                    size={"small"}
                    name={props.name}
                    label={""}
                    active={props.checked}
                    disabled={props.disabled}
                    fn={(e: any) => props.onChange(!!e.target.value)}
                />
            </div>
            {props.children && props.checked ? <div className={"WorkflowOption-nested"}>{props.children}</div> : null}
        </div>
    );
};

type EmailListProps = {
    value?: string;
    onChange: (value: string) => void;
    name: string;
    disabled?: boolean;
};

/** Comma-separated emails as removable chips plus an input that adds on Enter, comma or blur. */
export const WorkflowEmailList = (props: EmailListProps) => {
    const intl = useIntl();
    const [draft, setDraft] = useState("");
    const emails = splitEmails(props.value);

    const commit = () => {
        const candidate = draft.trim().replace(/,$/, "");

        if (candidate && !emails.includes(candidate)) {
            props.onChange(joinEmails([...emails, candidate]));
        }

        setDraft("");
    };

    return (
        <div className={"WorkflowEmails"} data-testid={`wf-emails-${props.name}`}>
            {emails.map((email) => (
                <span key={email} className={"WorkflowEmails-chip"} data-testid={`wf-email-${props.name}-${email}`}>
                    {email}
                    <button
                        type={"button"}
                        aria-label={intl.formatMessage({ id: "remove", defaultMessage: "Remove" }) + " " + email}
                        onClick={() => props.onChange(joinEmails(emails.filter((candidate) => candidate !== email)))}
                        disabled={props.disabled}>
                        <i className={"fad fa-xmark"} aria-hidden={true} />
                    </button>
                </span>
            ))}
            <input
                type={"email"}
                className={"WorkflowEmails-input"}
                value={draft}
                placeholder={intl.formatMessage({ id: "add_email_enter", defaultMessage: "Add an email, Enter" })}
                aria-label={intl.formatMessage({ id: "additional_recipients", defaultMessage: "Additional recipients" })}
                disabled={props.disabled}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        commit();
                    }
                }}
                data-testid={`wf-email-input-${props.name}`}
            />
        </div>
    );
};

type GroupProps = { icon: string; titleKey: string; helpKey?: string; children: React.ReactNode; testId: string };

const Group = (props: GroupProps) => (
    <div className={"WorkflowGroup"} data-testid={props.testId}>
        <div className={"WorkflowGroup-head"}>
            <span className={"WorkflowGroup-icon"}>
                <i className={`fad ${props.icon}`} aria-hidden={true} />
            </span>
            <div>
                <h5>
                    <OwLabel title={props.titleKey} noBottomMargin={true} />
                </h5>
                {props.helpKey ? (
                    <p>
                        <OwLabel title={props.helpKey} noBottomMargin={true} />
                    </p>
                ) : null}
            </div>
        </div>
        {props.children}
    </div>
);

type Props = {
    step: IOrder_Progress_State | null;
    allSteps: IOrder_Progress_State[];
    supplierModel: ISupplierModel;
    isFinal: boolean;
    canBeFinal: boolean;
    onUpdate: (id: OrderStatesEnum, patch: Partial<IOrder_Progress_State>) => void;
    onRemove: (id: OrderStatesEnum) => void;
    onSetFinal: (id: OrderStatesEnum, isFinal: boolean) => void;
};

/** The right column: the selected step's options, grouped, showing only what this business has turned on. */
export const WorkflowStepPanel = (props: Props) => {
    const intl = useIntl();
    // The step whose Remove is awaiting confirmation; picking another step drops it.
    const [confirmingRemove, setConfirmingRemove] = useState<OrderStatesEnum | null>(null);
    const supplier = props.supplierModel;
    const integration: any = supplier.Supplier_Integration || {};
    const t = (id: string, values?: Record<string, any>) => intl.formatMessage({ id, defaultMessage: `_${id}_` }, values);

    if (!props.step) {
        return (
            <div className={"WorkflowPanel empty"} data-testid={"wf-panel-empty"}>
                <i className={"fad fa-diagram-project"} aria-hidden={true} />
                <div className={"WorkflowPanel-empty-title"}>
                    <OwLabel title={"pick_a_step_to_edit"} noBottomMargin={true} />
                </div>
                <div className={"WorkflowPanel-empty-help"}>
                    <OwLabel title={"help_text_pick_a_step"} noBottomMargin={true} />
                </div>
            </div>
        );
    }

    const step = props.step;
    const id = step.order_state_id;
    const position = props.allSteps.findIndex((candidate) => candidate.order_state_id === id) + 1;
    const set = (patch: Partial<IOrder_Progress_State>) => props.onUpdate(id, patch);
    const flag = (key: StepKey) => !!step[key];
    const toggle = (key: StepKey) => (checked: boolean) => set({ [key]: checked } as Partial<IOrder_Progress_State>);

    /** For flags that carry forward: who set it earlier, as a reason chip, or null. */
    const carried = (key: WorkflowCarryKey) => {
        const earlier = stepThatSets(props.allSteps, step, key);

        return earlier ? (
            <span className={"SettingsChip"} data-testid={`wf-reason-${key}`}>
                <i className={"fad fa-lock"} aria-hidden={true} />
                {t("set_at_step", { step: t(earlier.name) })}
            </span>
        ) : null;
    };

    const carryRow = (key: WorkflowCarryKey, labelKey: string, helpKey?: string) => {
        const reason = carried(key);

        return (
            <WorkflowOptionRow
                labelKey={labelKey}
                helpKey={helpKey}
                name={key}
                checked={flag(key)}
                disabled={!!reason}
                reason={reason}
                onChange={toggle(key)}
                supplierModel={supplier}
            />
        );
    };

    const readOnlyHolds =
        flag("isSupplierReadOnly") ||
        flag("isSupplierAdminReadOnly") ||
        !!stepThatSets(props.allSteps, step, "isSupplierReadOnly") ||
        !!stepThatSets(props.allSteps, step, "isSupplierAdminReadOnly");

    return (
        <div className={"WorkflowPanel"} data-testid={"wf-panel"} aria-label={t(step.name)}>
            <div className={"WorkflowPanel-head"}>
                <div>
                    <h4>
                        <OwLabel title={step.name} noBottomMargin={true} />
                        <span className={"SettingsChip"}>{t("step_x_of_y", { step: position, total: props.allSteps.length })}</span>
                        {props.isFinal ? (
                            <span className={"SettingsChip brand"}>
                                <i className={"fad fa-flag-checkered"} aria-hidden={true} />
                                <OwLabel title={"chip_final"} noBottomMargin={true} />
                            </span>
                        ) : null}
                    </h4>
                    <p>
                        <OwLabel
                            title={isPreliminaryStep(step) ? "help_text_preliminary_step" : "help_text_step_panel"}
                            noBottomMargin={true}
                        />
                    </p>
                </div>
                {!isPreliminaryStep(step) && (props.canBeFinal || props.isFinal) ? (
                    confirmingRemove === id ? (
                        <div className={"WorkflowPanel-confirm"} role={"alertdialog"} data-testid={"wf-remove-confirm"}>
                            <span>{t("remove_step_confirm", { step: t(step.name) })}</span>
                            <OwButton
                                title={"cancel"}
                                className={"btn-outline-secondary"}
                                fn={() => setConfirmingRemove(null)}
                                disableWidthOnClick={true}
                                testId={"wf-remove-cancel"}
                            />
                            <OwButton
                                title={"remove"}
                                className={"btn-danger"}
                                leftIconClass={"fad fa-trash-can"}
                                fn={() => {
                                    setConfirmingRemove(null);
                                    props.onRemove(id);
                                }}
                                disableWidthOnClick={true}
                                disableDblClickPrevention={true}
                                testId={"wf-remove-step-confirm"}
                            />
                        </div>
                    ) : (
                        <OwButton
                            title={"remove_step"}
                            className={"btn-link text-muted"}
                            leftIconClass={"fad fa-trash-can"}
                            fn={() => setConfirmingRemove(id)}
                            disableWidthOnClick={true}
                            testId={"wf-remove-step"}
                        />
                    )
                ) : null}
            </div>

            {isPreliminaryStep(step) ? null : (
                <>
                    <Group
                        icon={"fa-user-shield"}
                        titleKey={"who_can_move_order_here"}
                        helpKey={"help_text_who_can_move"}
                        testId={"wf-group-access"}>
                        <WorkflowOptionRow
                            labelKey={"step_admin_only"}
                            helpKey={"help_text_step_admin_only"}
                            name={"isAdminOnlyStep"}
                            checked={flag("isAdminOnlyStep")}
                            onChange={toggle("isAdminOnlyStep")}
                            supplierModel={supplier}
                        />
                        {supplier.has_customer_module ? (
                            <>
                                <WorkflowOptionRow
                                    labelKey={"step_vendor_can_advance"}
                                    name={"isVendorStep"}
                                    checked={flag("isVendorStep")}
                                    onChange={toggle("isVendorStep")}
                                    supplierModel={supplier}
                                />
                                <WorkflowOptionRow
                                    labelKey={"step_alert_vendor"}
                                    helpKey={"help_text_step_alert_vendor"}
                                    name={"alertVendor"}
                                    checked={flag("alertVendor")}
                                    onChange={toggle("alertVendor")}
                                    supplierModel={supplier}
                                />
                            </>
                        ) : null}
                        {props.canBeFinal || props.isFinal ? (
                            <WorkflowOptionRow
                                labelKey={"is_final_step"}
                                helpKey={"help_text_is_final_step"}
                                name={"isFinalStep"}
                                checked={props.isFinal}
                                onChange={(checked) => props.onSetFinal(id, checked)}
                                supplierModel={supplier}
                            />
                        ) : null}
                    </Group>

                    <Group
                        icon={"fa-eye-slash"}
                        titleKey={"read_only_from_this_step"}
                        helpKey={"help_text_read_only_from_this_step"}
                        testId={"wf-group-readonly"}>
                        {supplier.has_customer_module ? carryRow("isVendorReadOnly", "vendors") : null}
                        {carryRow("isSupplierReadOnly", "internal_users")}
                        {carryRow("isSupplierAdminReadOnly", "internal_admins")}
                        <WorkflowOptionRow
                            labelKey={"allow_file_upload_while_read_only"}
                            name={"allowFileUploadIfReadOnly"}
                            checked={flag("allowFileUploadIfReadOnly")}
                            disabled={!readOnlyHolds}
                            reason={
                                readOnlyHolds ? null : (
                                    <span className={"SettingsChip"} data-testid={"wf-reason-allowFileUploadIfReadOnly"}>
                                        <i className={"fad fa-lock"} aria-hidden={true} />
                                        {t("needs_read_only_role")}
                                    </span>
                                )
                            }
                            onChange={toggle("allowFileUploadIfReadOnly")}
                            supplierModel={supplier}
                        />
                    </Group>

                    <Group icon={"fa-envelope"} titleKey={"emails"} helpKey={"help_text_step_emails"} testId={"wf-group-emails"}>
                        <WorkflowOptionRow
                            labelKey={"send_email_at_this_step"}
                            name={"sendEmail"}
                            checked={flag("sendEmail")}
                            onChange={toggle("sendEmail")}
                            supplierModel={supplier}>
                            {RECIPIENT_KEYS.filter((recipient) => !recipient.when || recipient.when(supplier)).map((recipient) => (
                                <WorkflowOptionRow
                                    key={recipient.key as string}
                                    nested={true}
                                    labelKey={recipient.optionLabelKey}
                                    name={recipient.key as string}
                                    checked={flag(recipient.key)}
                                    onChange={toggle(recipient.key)}
                                    supplierModel={supplier}
                                />
                            ))}
                            {ATTACHMENT_KEYS.map((attachment) => (
                                <WorkflowOptionRow
                                    key={attachment.key as string}
                                    nested={true}
                                    labelKey={attachment.optionLabelKey}
                                    name={attachment.key as string}
                                    checked={flag(attachment.key)}
                                    onChange={toggle(attachment.key)}
                                    supplierModel={supplier}
                                />
                            ))}
                            <WorkflowOptionRow
                                nested={true}
                                labelKey={"let_user_add_custom_message"}
                                helpKey={"help_text_let_user_add_custom_message"}
                                name={"allowCustomMessage"}
                                checked={flag("allowCustomMessage")}
                                onChange={toggle("allowCustomMessage")}
                                supplierModel={supplier}
                            />
                            {supplier.has_purchase_orders ? (
                                <WorkflowOptionRow
                                    nested={true}
                                    keepVendorWord={true}
                                    labelKey={"split_emails_by_vendor_source"}
                                    helpKey={"help_text_split_emails_by_vendor_source"}
                                    name={"splitEmailsByVendorSource"}
                                    checked={flag("splitEmailsByVendorSource")}
                                    onChange={toggle("splitEmailsByVendorSource")}
                                    supplierModel={supplier}
                                />
                            ) : null}
                            <div className={"WorkflowOption nested emails"}>
                                <div className={"WorkflowOption-text"}>
                                    <div className={"WorkflowOption-label"}>
                                        <OwLabel title={"additional_recipients"} noBottomMargin={true} />
                                    </div>
                                </div>
                                <div className={"WorkflowOption-control wrap"}>
                                    <WorkflowEmailList name={"email"} value={step.email} onChange={(value) => set({ email: value })} />
                                </div>
                            </div>
                        </WorkflowOptionRow>
                        <WorkflowOptionRow
                            labelKey={"email_packing_list_separately"}
                            helpKey={"help_text_email_packing_list_separately"}
                            name={"emailPDFPackingList"}
                            checked={flag("emailPDFPackingList")}
                            onChange={toggle("emailPDFPackingList")}
                            supplierModel={supplier}>
                            <div className={"WorkflowOption nested emails"}>
                                <div className={"WorkflowOption-text"}>
                                    <div className={"WorkflowOption-label"}>
                                        <OwLabel title={"packing_list_recipients"} noBottomMargin={true} />
                                    </div>
                                </div>
                                <div className={"WorkflowOption-control wrap"}>
                                    <WorkflowEmailList
                                        name={"emailPDFPackingListTo"}
                                        value={step.emailPDFPackingListTo}
                                        onChange={(value) => set({ emailPDFPackingListTo: value })}
                                    />
                                </div>
                            </div>
                        </WorkflowOptionRow>
                    </Group>

                    {supplier.has_payments ? (
                        <Group icon={"fa-credit-card"} titleKey={"payments"} testId={"wf-group-payments"}>
                            <WorkflowOptionRow
                                labelKey={"collect_payment_at_this_step"}
                                name={"collectPayment"}
                                checked={flag("collectPayment")}
                                disabled={!!carried("collectPayment")}
                                reason={carried("collectPayment")}
                                onChange={toggle("collectPayment")}
                                supplierModel={supplier}>
                                <WorkflowOptionRow
                                    nested={true}
                                    labelKey={"only_store_card"}
                                    name={"onlyCollectCcToUse"}
                                    checked={flag("onlyCollectCcToUse")}
                                    onChange={toggle("onlyCollectCcToUse")}
                                    supplierModel={supplier}
                                />
                            </WorkflowOptionRow>
                        </Group>
                    ) : null}

                    {integration.has_taxjar ||
                    integration.has_qbo ||
                    integration.has_qbd ||
                    integration.has_unanet ||
                    integration.has_woocommerce ||
                    supplier.has_commissions ||
                    supplier.has_purchase_orders ? (
                        <Group
                            icon={"fa-plug"}
                            titleKey={"automation_integrations"}
                            helpKey={"help_text_automation_integrations"}
                            testId={"wf-group-automation"}>
                            {integration.has_qbo ? (
                                <WorkflowOptionRow
                                    labelKey={"step_create_qbo_invoice"}
                                    name={"autoCreateQBOInvoice"}
                                    checked={flag("autoCreateQBOInvoice")}
                                    onChange={toggle("autoCreateQBOInvoice")}
                                    supplierModel={supplier}>
                                    <WorkflowOptionRow
                                        nested={true}
                                        labelKey={"step_email_qbo_invoice"}
                                        name={"autoEmailQBOInvoice"}
                                        checked={flag("autoEmailQBOInvoice")}
                                        onChange={toggle("autoEmailQBOInvoice")}
                                        supplierModel={supplier}
                                    />
                                </WorkflowOptionRow>
                            ) : null}
                            {integration.has_qbo && integration.has_vistatrac ? (
                                <WorkflowOptionRow
                                    labelKey={"step_create_qbo_estimate"}
                                    name={"autoCreateQBOEstimate"}
                                    checked={flag("autoCreateQBOEstimate")}
                                    onChange={toggle("autoCreateQBOEstimate")}
                                    supplierModel={supplier}
                                />
                            ) : null}
                            {integration.has_qbd ? (
                                <>
                                    <WorkflowOptionRow
                                        labelKey={"step_queue_qbd_sales_order"}
                                        name={"autoQueueSalesOrderForQBD"}
                                        checked={flag("autoQueueSalesOrderForQBD")}
                                        onChange={toggle("autoQueueSalesOrderForQBD")}
                                        supplierModel={supplier}
                                    />
                                    <WorkflowOptionRow
                                        labelKey={"step_queue_qbd_invoice"}
                                        name={"autoQueueInvoiceForQBD"}
                                        checked={flag("autoQueueInvoiceForQBD")}
                                        onChange={toggle("autoQueueInvoiceForQBD")}
                                        supplierModel={supplier}
                                    />
                                </>
                            ) : null}
                            {integration.has_taxjar ? (
                                <WorkflowOptionRow
                                    labelKey={"step_submit_taxjar"}
                                    name={"submitOrderToTaxJar"}
                                    checked={flag("submitOrderToTaxJar")}
                                    onChange={toggle("submitOrderToTaxJar")}
                                    supplierModel={supplier}
                                />
                            ) : null}
                            {integration.has_unanet ? (
                                <WorkflowOptionRow
                                    labelKey={"step_send_unanet"}
                                    name={"sendToUnanet"}
                                    checked={flag("sendToUnanet")}
                                    onChange={toggle("sendToUnanet")}
                                    supplierModel={supplier}
                                />
                            ) : null}
                            {integration.has_woocommerce ? (
                                <WorkflowOptionRow
                                    labelKey={"step_create_woocommerce"}
                                    name={"createOrderInWooCommerce"}
                                    checked={flag("createOrderInWooCommerce")}
                                    onChange={toggle("createOrderInWooCommerce")}
                                    supplierModel={supplier}
                                />
                            ) : null}
                            {supplier.has_commissions ? (
                                <WorkflowOptionRow
                                    labelKey={"step_release_commission"}
                                    name={"releaseCommission"}
                                    checked={flag("releaseCommission")}
                                    onChange={toggle("releaseCommission")}
                                    supplierModel={supplier}
                                />
                            ) : null}
                            {supplier.has_purchase_orders ? (
                                <WorkflowOptionRow
                                    labelKey={"step_create_pos"}
                                    helpKey={"help_text_auto_create_pos_from_order"}
                                    name={"autoCreatePOsFromOrder"}
                                    checked={flag("autoCreatePOsFromOrder")}
                                    onChange={toggle("autoCreatePOsFromOrder")}
                                    supplierModel={supplier}>
                                    <WorkflowOptionRow
                                        nested={true}
                                        labelKey={"step_email_pos"}
                                        helpKey={"help_text_auto_email_pos_on_create"}
                                        name={"autoEmailPOsOnCreate"}
                                        checked={flag("autoEmailPOsOnCreate")}
                                        onChange={toggle("autoEmailPOsOnCreate")}
                                        supplierModel={supplier}
                                    />
                                </WorkflowOptionRow>
                            ) : null}
                        </Group>
                    ) : null}

                    {supplier.has_shipments ||
                    supplier.has_service_levels ||
                    supplier.has_lead_times ||
                    supplier.has_serials ||
                    supplier.has_remakes ? (
                        <Group icon={"fa-truck"} titleKey={"fulfillment_inventory"} testId={"wf-group-fulfillment"}>
                            {supplier.has_shipments ? (
                                <WorkflowOptionRow
                                    labelKey={"step_allow_create_fulfillment"}
                                    helpKey={"help_text_step_allow_create_fulfillment"}
                                    name={"allowCreateFulfillment"}
                                    checked={flag("allowCreateFulfillment")}
                                    onChange={toggle("allowCreateFulfillment")}
                                    supplierModel={supplier}
                                />
                            ) : null}
                            {supplier.has_service_levels || supplier.has_lead_times
                                ? carryRow("calculateShippingAt", "step_calculate_shipping")
                                : null}
                            {supplier.has_serials ? (
                                <WorkflowOptionRow
                                    labelKey={"step_require_serials"}
                                    name={"requireSerialsAssigned"}
                                    checked={flag("requireSerialsAssigned")}
                                    onChange={toggle("requireSerialsAssigned")}
                                    supplierModel={supplier}
                                />
                            ) : null}
                            {supplier.has_remakes ? carryRow("allowRemake", "step_allow_remake") : null}
                        </Group>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default WorkflowStepPanel;
